import { api, ApiError } from "../api.ts";
import { gameById, getCatalogue, PREPLAY_LABEL, type Catalogue, type Game } from "../catalogue.ts";
import { actionButton, chips, gameGrid } from "../components.ts";
import { claimHref, parseUpdates, TAKEDOWN_LABEL, TAKEDOWN_REASONS, takedownBody } from "../listing.ts";
import { coverUrl, isUpdated, updatedText, versionText } from "../rows.ts";
import { clear, errorBox, fmtDate, fmtPlaytime, h, link, toast } from "../dom.ts";
import { setTitle } from "../router.ts";
import { currentMe, signedIn } from "../session.ts";
import { selling } from "../selling.ts";
import { playerTabs } from "../notes.ts";
import { gameBangers } from "../people.ts";
import { creatorCard } from "../zones/games/creator.ts";
import { hauntChip, hauntGames } from "../zones/games/haunt.ts";
import { titleHero } from "../zones/games/hero.ts";
import { mediaStrip } from "../zones/games/media.ts";
import { similarGames } from "../zones/games/more.ts";
import { ensureTitleStyle } from "../zones/games/style.ts";
import { MAIN_SECTIONS, SIDE_SECTIONS } from "../zones/games/template.ts";
import { accentFor, washFor } from "../zones/games/theme.ts";
import { zoneNow } from "../zones/open-haunt/season.ts";

export interface Review {
  id: string;
  user_id: string;
  recommended: boolean;
  body: string;
  version: string;
  playtime_seconds: number;
  previous_versions: string[];
  burst: boolean;
  created_at: number;
  updated_at: number;
  /** the developer's reply, and "Addressed in vX" (community 0007, docs/build/GAME-COMMUNITY.md) */
  reply?: { body: string; at: number | null } | null;
  addressed?: { version: string; change: string | null } | null;
}

export interface ReviewSummary {
  total: number;
  counted: number;
  positive: number;
  percent_positive: number | null;
  excluded_burst: number;
  burst_active: boolean;
  min_playtime_seconds: number;
}

interface PublicUser {
  id: string;
  handle: string | null;
  displayName: string | null;
}

const names = new Map<string, Promise<string>>();
const label = (u: PublicUser | undefined) => u?.displayName ?? (u?.handle ? `@${u.handle}` : "Player");
let batch: Map<string, (name: string) => void> | null = null;

/**
 * Display name for a user id, as this viewer may see it. Signed in, ids are batched into one
 * `POST /users/lookup` (accounts, wave 2); otherwise, or on an older accounts, one public read per user.
 */
export function userName(id: string): Promise<string> {
  let p = names.get(id);
  if (p) return p;
  if (!signedIn()) {
    p = api<PublicUser>("accounts", `/users/${encodeURIComponent(id)}`, { auth: false }).then(label, () => "Player");
  } else {
    p = new Promise<string>((resolve) => {
      if (!batch) {
        const mine = (batch = new Map());
        queueMicrotask(async () => {
          batch = null;
          const ids = [...mine.keys()];
          try {
            const r = await api<{ users: PublicUser[] }>("accounts", "/users/lookup", { body: { ids: ids.slice(0, 100) } });
            for (const id of ids) mine.get(id)!(label(r.users.find((u) => u.id === id)));
          } catch {
            for (const id of ids) api<PublicUser>("accounts", `/users/${encodeURIComponent(id)}`).then(label, () => "Player").then(mine.get(id)!);
          }
        });
      }
      batch.set(id, resolve);
    });
  }
  names.set(id, p);
  return p;
}

export function nameSpan(id: string): HTMLElement {
  const el = h("span", { class: "who" }, "Player");
  void userName(id).then((n) => (el.textContent = n));
  return el;
}

export function summaryLine(s: ReviewSummary): string {
  if (s.counted === 0) return s.total ? "No counted reviews yet" : "No reviews yet";
  return `${s.percent_positive ?? 0}% recommend · ${s.counted} ${s.counted === 1 ? "review" : "reviews"}`;
}

export async function gamePage(params: Record<string, string>): Promise<HTMLElement> {
  const cat = await getCatalogue();
  const g = gameById(cat, params.id ?? "");
  if (!g) throw new Error("That game isn't in the catalogue.");
  setTitle(g.title);
  ensureTitleStyle();

  // the template (zones/games/template.ts): hero, then the main column and the side column, then More like this;
  // every section returns null (or stays hidden) when it has nothing to show
  const haunt = hauntGames(cat, zoneNow());
  const main: Record<(typeof MAIN_SECTIONS)[number], () => HTMLElement | null> = {
    media: () => mediaStrip(g),
    about: () => aboutSection(g),
    "whats-new": () => whatsNew(g),
    bangers: () => gameBangers(g.id, g.creator.claimedBy ? g.creator.id : null),
    players: () => playerTabs(g),
    reviews: () => reviewsSection(g),
  };
  const side: Record<(typeof SIDE_SECTIONS)[number], () => HTMLElement | null> = {
    // the creator first; the claim offer is a quiet note under it, not the page's loudest box
    creator: () => creatorCard(g, cat, tipBox(g, currentMe()?.id ?? null)),
    community: () => communitySide(g),
    claim: () => (g.claimable ? claimBox(g) : null),
  };
  const blur = coverUrl(g, "blur");
  return h(
    "article",
    // custom properties go in as a style string (validated hex colours; art paths are [a-z0-9-] keys)
    { class: "game gt", style: `--gt-accent: ${accentFor(g.palette)}; --gt-wash: ${washFor(g.palette)};${blur ? ` --gt-wash-img: url("${blur}");` : ""}` },
    h("div", { class: "gt-wash", "aria-hidden": "true" }),
    titleHero(g, [hauntChip(g, haunt), g.staffPick ? h("span", { class: "gt-chip" }, "Staff pick") : null], versionLine(g)),
    main.media(),
    h(
      "div",
      { class: "game-body" },
      h("div", { class: "game-main" }, MAIN_SECTIONS.filter((s) => s !== "media").map((s) => main[s]())),
      h("aside", { class: "game-side" }, SIDE_SECTIONS.map((s) => side[s]())),
    ),
    moreLikeThis(cat, g, haunt),
  );
}

export { similarGames };

/** "Before you play: Cookie banner." with a slow first load said plainly, not as a warning (review 2026-10-19 #5). */
export function prePlayLine(pre: readonly string[]): string {
  const slow = pre.includes("long_load") ? "Takes a minute to load the first time." : "";
  const rest = pre.filter((p) => p !== "long_load").map((p) => PREPLAY_LABEL[p] ?? p);
  return [rest.length ? `Before you play: ${rest.join(", ")}.` : "", slow].filter(Boolean).join(" ");
}

function aboutSection(g: Game): HTMLElement | null {
  if (!g.blurb && !g.note) return null;
  return h(
    "section",
    { class: "gt-about" },
    h("h2", { class: "gt-sr" }, "About the game"),
    g.blurb ? h("p", { class: "lead" }, g.blurb) : null,
    g.note ? h("p", { class: "dim" }, g.note) : null,
    chips(g),
    g.facets.prePlay.length
      ? h("p", { class: "dim small" }, prePlayLine(g.facets.prePlay))
      : null,
  );
}

function moreLikeThis(cat: Catalogue, g: Game, haunt: ReadonlySet<string>): HTMLElement | null {
  const list = similarGames(cat, g, 10, haunt);
  if (list.length < 2) return null;
  return h(
    "section",
    { class: "section more-like gt-more" },
    h("div", { class: "section-head" }, h("h2", null, "More like this"), link(`/browse?kind=${g.kind}&genre=${encodeURIComponent(g.genre)}`, { class: "more" }, "See all")),
    gameGrid(list),
  );
}

/** Under the creator: the Updated badge for 7 days after a confirmed update, or the version label. */
export function versionLine(g: Game, nowSec = Date.now() / 1000): HTMLElement | null {
  if (isUpdated(g, nowSec)) return h("p", { class: "version-line" }, h("span", { class: "badge new updated" }, updatedText(g)));
  const v = versionText(g.versionLabel);
  return v ? h("p", { class: "version-line dim small" }, `Version ${v}`) : null;
}

/** "What's new": the latest confirmed update's notes, or its link (accounts `GET /catalogue/games/:id/updates`). */
function whatsNew(g: Game): HTMLElement | null {
  if (!g.versionUpdatedAt) return null;
  const host = h("section", { class: "section whats-new", hidden: true });
  void api<unknown>("accounts", `/catalogue/games/${encodeURIComponent(g.id)}/updates?limit=3`, { auth: false })
    .then((r) => {
      const latest = parseUpdates(r)[0];
      if (!latest || (!latest.notes && !latest.notesUrl)) return;
      const v = versionText(latest.versionLabel);
      clear(
        host,
        h("h2", null, "What's new"),
        h("p", { class: "dim small" }, [v, fmtDate(latest.detectedAt)].filter(Boolean).join(" · ")),
        latest.notes ? h("p", { class: "notes" }, latest.notes) : null,
        latest.notesUrl ? h("p", { class: "small" }, h("a", { href: latest.notesUrl, rel: "noopener noreferrer", target: "_blank" }, "Read the full notes")) : null,
      );
      host.hidden = false;
    })
    .catch(() => {});
  return host;
}

/** "Is this your game?" for an unclaimed listing: claim it (the dashboard's flow) or ask us to take it down. */
function claimBox(g: Game): HTMLElement {
  const msg = h("div", { role: "status" });
  const contactField = signedIn()
    ? null
    : h("label", { class: "stack small" }, "Your email (we reply there)", h("input", { type: "email", name: "contact", required: true, autocomplete: "email", maxlength: 254 }));
  const form: HTMLFormElement = h(
    "form",
    {
      class: "stack",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const fd = new FormData(form);
        const r = takedownBody({ reason: String(fd.get("reason") ?? ""), message: String(fd.get("message") ?? ""), contact: String(fd.get("contact") ?? "") }, signedIn());
        if ("error" in r) return clear(msg, h("p", { class: "error", role: "alert" }, r.error));
        try {
          await api("accounts", `/catalogue/games/${encodeURIComponent(g.id)}/takedown`, { body: r.body, auth: signedIn() });
          clear(form, h("p", null, "Thanks. A moderator will look at it and reply. Until then the game stays listed."));
        } catch (err) {
          clear(msg, errorBox(err));
        }
      },
    },
    h(
      "label",
      { class: "stack small" },
      "Why should it come off Banger?",
      h("select", { name: "reason", required: true }, TAKEDOWN_REASONS.map((r) => h("option", { value: r }, TAKEDOWN_LABEL[r]))),
    ),
    h("label", { class: "stack small" }, "Anything we should know (optional)", h("textarea", { name: "message", rows: 3, maxlength: 2000 })),
    contactField,
    h("button", { type: "submit", class: "btn small ghost" }, "Ask to remove it"),
    msg,
  );
  return h(
    "section",
    // the launcher links here for claimable games: https://banger.sh/g/<g_id>#claim
    { class: "claim-box claim-quiet", id: "claim" },
    h("h2", null, "Is this your game?"),
    h("p", { class: "small dim" }, "We listed it from the web, linking to your own site. Claim it to get stats, release notes and a waitlist, or remove it."),
    h("p", { class: "small" }, link(claimHref(g), { class: "claim-link" }, "Claim it"), " · ", h("span", { class: "dim" }, "or ask us to remove it below")),
    h("details", { class: "remove-it" }, h("summary", null, "Remove it"), form),
  );
}

/** Who sees "Tip the creator": only for a claimed creator (money pays the claimant), and never on your own game. */
export function canTip(g: Pick<Game, "creator">, viewerId: string | null): boolean {
  return !!g.creator.claimedBy && g.creator.claimedBy !== viewerId;
}

/** The line under "Tip the creator". */
export function tipNote(creator: string): string {
  return `${creator} gets 80% of your tip after tax; Banger keeps 20%, as on every sale. Tips come from your Banger balance and can't be refunded.`;
}

/** Tipping happens in Threedom (the website is browse only): the button opens the game there with the tip action. */
function tipBox(g: Game, viewerId: string | null): HTMLElement | null {
  if (!selling() || !canTip(g, viewerId)) return null; // no tips while nothing is sold (wave 9)
  return h(
    "div",
    { class: "tip-box" },
    actionButton(g, "tip", "Tip the creator", "btn small"),
    // tips split like every sale: 80% of the amount after tax to the creator (CONTRACTS.md wave 10)
    h("p", { class: "dim small" }, tipNote(g.creator.name)),
  );
}

/**
 * A section that stays hidden until `fill` has something to show (it returns false for nothing): empty modules and
 * services that can't be reached don't take space on a player's page.
 */
export function quietSection(title: string, fill: (body: HTMLElement) => Promise<boolean>, cls = "section"): HTMLElement {
  const body = h("div", { class: "section-body" });
  const el = h("section", { class: cls, hidden: true }, h("h2", null, title), body);
  fill(body).then(
    (shown) => (el.hidden = !shown),
    () => (el.hidden = true),
  );
  return el;
}

function communitySide(g: Game): HTMLElement {
  return quietSection(
    "Community",
    async (body) => {
      const c = await api<{ counts: { threads: number; devlogs: number; open_bugs: number; clips: number }; latest_devlog: { id: string; title: string; created_at: number } | null }>(
        "community",
        `/games/${g.id}/community`,
        { auth: false },
      );
      if (!c.counts.threads && !c.counts.devlogs && !c.counts.open_bugs && !c.latest_devlog) return false;
      clear(
        body,
        h(
          "dl",
          { class: "stats" },
          h("div", null, h("dt", null, "Discussions"), h("dd", null, c.counts.threads)),
          h("div", null, h("dt", null, "Devlogs"), h("dd", null, c.counts.devlogs)),
          h("div", null, h("dt", null, "Open bugs"), h("dd", null, c.counts.open_bugs)),
        ),
        c.latest_devlog ? h("p", { class: "small" }, "Latest devlog: ", h("strong", null, c.latest_devlog.title), ` (${fmtDate(c.latest_devlog.created_at)})`) : null,
        h("p", { class: "dim small" }, "Bug reports and discussion happen in the Banger app, where the game runs."),
      );
      return true;
    },
    "section side-box",
  );
}

/** Reviews, once there are any: signed out, a game nobody has reviewed shows no reviews block at all. */
function reviewsSection(g: Game): HTMLElement {
  return quietSection("Reviews", async (body) => {
    const r = await api<{ summary: ReviewSummary; reviews: Review[] }>("community", `/games/${g.id}/reviews?limit=20`, { auth: false });
    const s = r.summary;
    if (!s.total && !r.reviews.length && !signedIn()) return false; // signed in, the block is where you write one
    clear(
      body,
      h(
        "div",
        { class: "review-summary" },
        h("strong", null, summaryLine(s)),
        h("span", { class: "dim small" }, ` Only players with ${fmtPlaytime(s.min_playtime_seconds)}+ of play can review.`),
        s.burst_active ? h("p", { class: "note" }, "A wave of reviews arrived at once; they stay visible but don't count until a moderator has looked.") : null,
      ),
      writeReview(g),
      r.reviews.length
        ? h(
            "ul",
            { class: "reviews" },
            r.reviews.map((rv) =>
              h(
                "li",
                { class: "review" },
                h(
                  "div",
                  { class: "review-head" },
                  h("span", { class: `verdict ${rv.recommended ? "yes" : "no"}` }, rv.recommended ? "Recommended" : "Not recommended"),
                  nameSpan(rv.user_id),
                  h("span", { class: "dim small" }, `${fmtPlaytime(rv.playtime_seconds)} played · v${rv.version} · ${fmtDate(rv.updated_at)}`),
                ),
                rv.body ? h("p", null, rv.body) : null,
                rv.previous_versions.length ? h("p", { class: "dim small" }, `Updated after v${rv.previous_versions.join(", v")}`) : null,
                rv.burst ? h("p", { class: "dim small" }, "Not counted in the score yet (review wave).") : null,
                rv.addressed
                  ? h("p", { class: "small verdict yes" }, `\u2713 Addressed in ${vLabel(rv.addressed.version)}${rv.addressed.change ? `: ${rv.addressed.change}` : ""}`)
                  : null,
                rv.reply
                  ? h(
                      "blockquote",
                      { class: "dev-reply" },
                      h("p", { class: "small" }, h("strong", null, g.creator.name), " \u00b7 developer", rv.reply.at ? ` \u00b7 ${fmtDate(rv.reply.at)}` : ""),
                      h("p", null, rv.reply.body),
                    )
                  : null,
                isCreatorOf(g) ? replyForm(g, rv) : null,
              ),
            ),
          )
        : null,
    );
    return true;
  });
}

const vLabel = (v: string) => (/^v/i.test(v) ? v : `v${v}`);

/** The signed-in viewer claimed this game: they can reply under reviews. */
function isCreatorOf(g: Game): boolean {
  const me = currentMe();
  return !!me && !!g.creator.claimedBy && g.creator.claimedBy === me.id;
}

/** The developer's reply under a review, and "Addressed in vX" (both empty clears it). */
function replyForm(g: Game, rv: Review): HTMLElement {
  const msg = h("div");
  const form = h(
    "form",
    {
      class: "stack",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const fd = new FormData(form);
        try {
          await api("community", `/games/${g.id}/reviews/${rv.id}/reply`, {
            method: "PUT",
            body: { body: String(fd.get("body") ?? ""), addressed_in: String(fd.get("addressed_in") ?? ""), change: String(fd.get("change") ?? "") },
          });
          toast("Reply saved");
          form.closest(".section")?.replaceWith(reviewsSection(g));
        } catch (err) {
          clear(msg, errorBox(err));
        }
      },
    },
    h("label", null, "Your reply", h("textarea", { name: "body", rows: "3", maxlength: "1000" }, rv.reply?.body ?? "")),
    h("label", null, "Addressed in version (optional)", h("input", { name: "addressed_in", maxlength: "64", placeholder: "v1.4", value: rv.addressed?.version ?? "" })),
    h("label", null, "What changed (optional)", h("input", { name: "change", maxlength: "200", value: rv.addressed?.change ?? "" })),
    h("button", { type: "submit" }, rv.reply || rv.addressed ? "Update reply" : "Reply"),
    msg,
  );
  return h("details", { class: "small" }, h("summary", null, rv.reply || rv.addressed ? "Edit your reply" : "Reply as the developer"), form);
}

function writeReview(g: Game): HTMLElement {
  if (!signedIn()) return h("p", { class: "small" }, link(`/signin?next=/g/${g.id}`, null, "Sign in"), " to review once you've played.");
  const msg = h("div");
  const form = h(
    "form",
    {
      class: "stack",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const fd = new FormData(form);
        try {
          await api("community", `/games/${g.id}/reviews/mine`, {
            method: "PUT",
            body: { recommended: fd.get("rec") === "yes", body: String(fd.get("body") ?? ""), version: String(fd.get("version") || "1.0") },
          });
          toast("Review saved");
          form.closest(".section")?.replaceWith(reviewsSection(g));
        } catch (err) {
          const text =
            err instanceof ApiError && err.code === "not_enough_playtime"
              ? "Play a bit longer in the Banger app first: reviews are from players only."
              : err instanceof ApiError && err.code === "own_game"
                ? "Creators can't review their own game."
                : null;
          clear(msg, text ? h("p", { class: "error", role: "alert" }, text) : errorBox(err));
        }
      },
    },
    h(
      "fieldset",
      { class: "row" },
      h("legend", null, "Your review"),
      h("label", { class: "check" }, h("input", { type: "radio", name: "rec", value: "yes", checked: true }), "Recommend"),
      h("label", { class: "check" }, h("input", { type: "radio", name: "rec", value: "no" }), "Don't recommend"),
    ),
    h("label", { for: "rv-body", class: "sr-only" }, "Review text"),
    h("textarea", { id: "rv-body", name: "body", rows: 3, maxlength: 8000, placeholder: "What should other players know?" }),
    h("label", { class: "inline small" }, "Version played ", h("input", { name: "version", value: "1.0", size: 6, maxlength: 20 })),
    h("button", { class: "btn", type: "submit" }, "Post review"),
    msg,
  );
  return h("details", { class: "write-review" }, h("summary", null, "Write a review"), form);
}
